/**
 * CityTownEditor - 都市/町編集ダイアログコンポーネント
 *
 * マップエディタで都市や町のプロパティを編集するためのダイアログ。
 */

import { useState, useEffect } from 'react';
import type { EditorCity, EditorTown } from './HexGrid';

// ============================================================================
// 型定義
// ============================================================================

type CityColor = 'red' | 'blue' | 'yellow' | 'purple' | 'black';
type DiceColor = 'white' | 'black';

export interface CityTownEditorProps {
  mode: 'city' | 'town';
  hexCoord: { q: number; r: number };
  existingCity?: EditorCity;
  existingTown?: EditorTown;
  onSave: (data: EditorCity | EditorTown) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

/**
 * 配列内の要素をトグル（追加/削除）
 */
function toggleArrayItem<T>(arr: T[], item: T): T[] {
  if (arr.includes(item)) {
    return arr.filter(x => x !== item);
  }
  return [...arr, item];
}

// ============================================================================
// 定数
// ============================================================================

const CITY_COLORS: { color: CityColor; label: string; cssColor: string }[] = [
  { color: 'red', label: 'Red', cssColor: 'hsl(var(--goods-red))' },
  { color: 'blue', label: 'Blue', cssColor: 'hsl(var(--goods-blue))' },
  { color: 'yellow', label: 'Yellow', cssColor: 'hsl(var(--goods-yellow))' },
  { color: 'purple', label: 'Purple', cssColor: 'hsl(var(--goods-purple))' },
  { color: 'black', label: 'Black', cssColor: 'hsl(var(--goods-black))' },
];

// ============================================================================
// CityTownEditor コンポーネント
// ============================================================================

export function CityTownEditor({
  mode,
  hexCoord,
  existingCity,
  existingTown,
  onSave,
  onCancel,
  onDelete,
}: CityTownEditorProps) {
  // City state - 複数選択対応
  const [cityName, setCityName] = useState(existingCity?.name || '');
  const [cityColors, setCityColors] = useState<CityColor[]>(existingCity?.colors || ['yellow']);
  const [diceNumbers, setDiceNumbers] = useState<number[]>(existingCity?.diceNumbers || []);
  const [diceColors, setDiceColors] = useState<DiceColor[]>(existingCity?.diceColors || ['white']);

  // Town state
  const [townName, setTownName] = useState(existingTown?.name || '');
  const [urbanizable, setUrbanizable] = useState(existingTown?.urbanizable ?? true);

  // Reset form when mode or existing data changes
  useEffect(() => {
    if (mode === 'city') {
      setCityName(existingCity?.name || '');
      setCityColors(existingCity?.colors || ['yellow']);
      setDiceNumbers(existingCity?.diceNumbers || []);
      setDiceColors(existingCity?.diceColors || ['white']);
    } else {
      setTownName(existingTown?.name || '');
      setUrbanizable(existingTown?.urbanizable ?? true);
    }
  }, [mode, existingCity, existingTown]);

  // ダイス目の追加/削除（空配列も許容）
  const toggleDiceNumber = (num: number) => {
    if (diceNumbers.includes(num)) {
      setDiceNumbers(diceNumbers.filter(n => n !== num).sort((a, b) => a - b));
    } else {
      setDiceNumbers([...diceNumbers, num].sort((a, b) => a - b));
    }
  };

  // 都市色の追加/削除（最低1つは必要）
  const toggleCityColor = (color: CityColor) => {
    const newColors = toggleArrayItem(cityColors, color);
    if (newColors.length > 0) {
      setCityColors(newColors);
    }
  };

  // ダイス色の追加/削除（最低1つは必要）
  const toggleDiceColor = (color: DiceColor) => {
    const newColors = toggleArrayItem(diceColors, color);
    if (newColors.length > 0) {
      setDiceColors(newColors);
    }
  };

  const handleSave = () => {
    if (mode === 'city') {
      if (!cityName.trim()) {
        alert('City name is required');
        return;
      }
      if (cityColors.length === 0) {
        alert('At least one city color is required');
        return;
      }
      if (diceColors.length === 0) {
        alert('At least one dice color is required');
        return;
      }
      onSave({
        name: cityName.trim().toUpperCase(),
        colors: cityColors,
        diceNumbers,
        diceColors,
      });
    } else {
      if (!townName.trim()) {
        alert('Town name is required');
        return;
      }
      onSave({
        name: townName.trim().toUpperCase(),
        urbanizable,
      });
    }
  };

  const isEditing = mode === 'city' ? !!existingCity : !!existingTown;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-[400px] max-w-[90vw]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? 'Edit' : 'Add'} {mode === 'city' ? 'City' : 'Town'}
          </h2>
          <span className="text-sm text-muted-foreground">
            Hex: ({hexCoord.q}, {hexCoord.r})
          </span>
        </div>

        {/* City Form */}
        {mode === 'city' && (
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1">City Name</label>
              <input
                type="text"
                value={cityName}
                onChange={(e) => setCityName(e.target.value)}
                placeholder="e.g., BRIDGETOWN"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
            </div>

            {/* Colors - 複数選択 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                City Colors <span className="text-xs text-muted-foreground">- click to toggle (1+)</span>
              </label>
              <div className="flex gap-2">
                {CITY_COLORS.map(({ color, label, cssColor }) => (
                  <button
                    key={color}
                    className={`
                      w-10 h-10 rounded-md border-2 transition-all
                      ${cityColors.includes(color)
                        ? 'ring-2 ring-primary ring-offset-2'
                        : 'border-transparent hover:border-border'
                      }
                    `}
                    style={{ backgroundColor: cssColor }}
                    onClick={() => toggleCityColor(color)}
                    title={label}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {cityColors.join(', ') || 'none'}
              </p>
            </div>

            {/* Dice Numbers (複数選択可) */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Dice Numbers (1-6) <span className="text-xs text-muted-foreground">- click to toggle</span>
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <button
                    key={num}
                    className={`
                      w-10 h-10 rounded-md border-2 text-lg font-bold
                      transition-all
                      ${diceNumbers.includes(num)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted border-border hover:border-primary'
                      }
                    `}
                    onClick={() => toggleDiceNumber(num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {diceNumbers.join(', ')}
              </p>
            </div>

            {/* Dice Colors - 複数選択 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Dice Background Color <span className="text-xs text-muted-foreground">- click to toggle (1+)</span>
              </label>
              <div className="flex gap-3">
                <button
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
                    ${diceColors.includes('white')
                      ? 'ring-2 ring-primary ring-offset-2 border-gray-400'
                      : 'border-gray-300 hover:border-gray-400'
                    }
                  `}
                  style={{ backgroundColor: 'white', color: 'black' }}
                  onClick={() => toggleDiceColor('white')}
                >
                  <span className="w-4 h-4 rounded-full bg-white border border-gray-400"></span>
                  White
                </button>
                <button
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
                    ${diceColors.includes('black')
                      ? 'ring-2 ring-primary ring-offset-2 border-gray-600'
                      : 'border-gray-600 hover:border-gray-500'
                    }
                  `}
                  style={{ backgroundColor: '#1a1a1a', color: 'white' }}
                  onClick={() => toggleDiceColor('black')}
                >
                  <span className="w-4 h-4 rounded-full bg-black border border-gray-600"></span>
                  Black
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {diceColors.join(', ') || 'none'}
              </p>
            </div>
          </div>
        )}

        {/* Town Form */}
        {mode === 'town' && (
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1">Town Name</label>
              <input
                type="text"
                value={townName}
                onChange={(e) => setTownName(e.target.value)}
                placeholder="e.g., BRIGHTON"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
            </div>

            {/* Urbanizable */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="urbanizable"
                checked={urbanizable}
                onChange={(e) => setUrbanizable(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="urbanizable" className="text-sm">
                Urbanizable (can be converted to a new city)
              </label>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between mt-6 pt-4 border-t border-border">
          <div>
            {isEditing && onDelete && (
              <button
                className="px-4 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                onClick={onDelete}
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
              onClick={handleSave}
            >
              {isEditing ? 'Save' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CityTownEditor;
